import React, { useEffect } from "react";
import { Box, Grid, Typography, Card, CardActionArea } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import useMetaTracking from "../hooks/useMetaTracking";
import Footer from "../Components/Footer/Footer";
import MenuCard from "../Components/MenuCards/MenuCards";

// Nav icons
import San from "../Assets/nav_icons/sandwich.png";
import Cro from "../Assets/nav_icons/croissant.png";
import Bao from "../Assets/nav_icons/bao.png";
import XoiIcon from "../Assets/nav_icons/xoi.png";
import Dri from "../Assets/nav_icons/drink.png";

// --- Images (Corrected Paths) ---
import VeganLemongrassSteak from "../Assets/vegan_lemongrass_steak.png";
import VeganHamCheeseCroissantSandwich from "../Assets/vegan_ham_cheese_croissant_sandwich_1200x900.png";
import VeganChickenCroissantSandwich from "../Assets/vegan_chicken_croissant_sandwich_1200x900.png";
import VeganPorkMeatballs from "../Assets/vegan_pork_meatballs.png";
import VeganGrilledPork from "../Assets/vegan_grilled_pork.png";
import VeganGrilledChicken from "../Assets/vegan_grilled_chicken.png";
import VeganSpecialCombo from "../Assets/vegan_special_combo.png";
import VeganPateeMeatloaf from "../Assets/vegan_pate_meatloaf.png";
import VeganShreddedPorkSkin from "../Assets/vegan_shredded_pork_skin.png";
import VeganFriedChicken from "../Assets/vegan_ga_chien.png";
import VietnameseBaguette from "../Assets/vietnamese_baguette.png";

// Pastries
import VeganPistachioCroissant from "../Assets/vegan_pistachio_croissant.png";
import VeganChickenCroissant from "../Assets/vegan_chicken_croissant.png";
import VeganCroissant from "../Assets/new_vegan_croissant.png";
import VeganCoconutCroissant from "../Assets/vegan_coconut_croissant.png";
import VeganHamAndCheeseCroissant from "../Assets/vegan_ham_and_cheese_croissant.png";
import VeganHamAndCheeseCroissantJalapeno from "../Assets/vegan_ham_and_cheese_croissant_jalapeno.png";
import VeganGuavaTurnover from "../Assets/dhv_guava_turnover.png";
import VeganAppleTurnover from "../Assets/vegan_apple_turnover.png";
import VeganPineappleTurnover from "../Assets/vegan_pineapple_turnover.png";
import VeganAlmondCroissant from "../Assets/vegan_almond_croissant.png";

// Vietnamese Snacks
import VeganVietnameseDonut from "../Assets/vegan_vietnamese_donut.png";
import VeganSteamedBun from "../Assets/new_vegan_banh_bao.png";
import VeganPatechaud from "../Assets/new_vegan_pate_chaud.png";
import VeganBeefPuffPastry from "../Assets/vegan_beef_patechaud_1200x900.png";
import VeganSavoryEmpanada from "../Assets/vegan_empanada_1200x900.png";
import VeganSweetEmpanada from "../Assets/vegan_sweet_empanada.png";
import VeganSoftMiniBuns from "../Assets/vegan_soft_mini_buns.png";

// Drinks
import VeganVietnameseCoffee from "../Assets/vegan_vietnamese_coffee.png";
import VietnameseBeautyElixir from "../Assets/vietnamese_beauty_elixir.png";
import SamBoLuong from "../Assets/sam_bo_luong.png";
import PennywortJuice from "../Assets/pennywort_juice.png";

// Xôi
import XoiPandan from "../Assets/vegan_pandan_xoi_1200x900.png";
import XoiMagenta from "../Assets/vegan_magenta_leaf_1200x900.png";
import XoiVeganChicken from "../Assets/new_vegan_chicken_sticky_rice.png";

function VeganMenu() {
  useMetaTracking("ViewContent", {
    content_name: "Vegan Menu",
    content_category: "Vegan Menu",
    content_ids: ["vegan_menu"],
    content_type: "vegan_menu",
  });

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [location]);

  // Reusable nav item
  const NavItem = ({ icon, label, targetId }) => (
    <Card
      sx={{
        background: "transparent",
        color: "#fff",
        boxShadow: "none",
        borderRadius: 0,
        width: { xs: "100%", md: "auto" },
      }}
    >
      <CardActionArea
        disableRipple
        onClick={() => {
          const section = document.getElementById(targetId);
          if (section) section.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          p: 2,
          "&:hover": {
            background: "transparent !important",
            "& .navText": { fontWeight: 800 },
          },
          "& .MuiCardActionArea-focusHighlight": {
            backgroundColor: "transparent !important",
            opacity: 0,
          },
        }}
      >
        <img src={icon} alt={label} style={{ width: 40, marginBottom: 8 }} />
        <Typography
          className="navText"
          variant="body1"
          sx={{
            fontSize: { xs: 13, md: 14 },
            fontWeight: 300,
            textTransform: "uppercase",
            textAlign: "center",
            whiteSpace: { xs: "normal", md: "nowrap" },
          }}
        >
          {label}
        </Typography>
      </CardActionArea>
    </Card>
  );

  return (
    <>
      <Helmet>
        <title>Vegan Menu | Đông Hưng Viên Bakery</title>
      </Helmet>

      {/* Header */}
      <Box sx={{ background: "#000 !important" }}>
        <Grid container>
          <Grid item xs={12}>
            <Typography
              variant="h1"
              sx={{
                mb: 3,
                lineHeight: 2,
                textAlign: "center",
                px: 2,
                pt: 15,
                color: "#fff",
                fontWeight: 700,
                fontSize: "35px",
                fontFamily: "Montserrat, Helvetica, Arial, Lucida, sans-serif",
                textTransform: "uppercase",
                letterSpacing: "5px",
              }}
            >
              Vegan Menu
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Nav Bar */}
      <Box sx={{ display: "flex", justifyContent: "center", background: "#000" }}>
        <Box
          sx={{
            maxWidth: "1080px",
            width: "90%",
            display: "flex",
            alignItems: "center",
            flexWrap: { xs: "wrap", md: "nowrap" },
            justifyContent: { xs: "center", md: "space-between" },
            columnGap: { md: 2 },
            rowGap: { xs: 1 },
            pb: 1,
          }}
        >
          <NavItem icon={San} label="VEGAN BÁNH MÌ / SANDWICHES" targetId="vegan-banh-mi-section" />
          <NavItem icon={Cro} label="VEGAN PASTRIES" targetId="vegan-pastries" />
          <NavItem icon={Bao} label="VEGAN VIETNAMESE SNACKS" targetId="vegan-vietnamese-snacks" />
          <NavItem icon={XoiIcon} label="VEGAN XÔI" targetId="vegan-xoi" />
          <NavItem icon={Dri} label="VEGAN DRINKS" targetId="vegan-drinks" />
        </Box>
      </Box>

      {/* VEGAN BÁNH MÌ */}
      <Box
        id="vegan-banh-mi-section"
        sx={{ display: "flex", justifyContent: "center", background: "#000", pt: 10 }}
      >
        <Box sx={{ maxWidth: "1080px", width: "90%" }}>
          <Grid container spacing={0}>
            <Grid item lg={3} md={12} sm={12} xs={12}>
              <Typography
                variant="h1"
                sx={{
                  mb: 3,
                  lineHeight: "1.2em",
                  px: 2,
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "30px",
                  textTransform: "uppercase",
                  textAlign: { xs: "center", lg: "left" },
                }}
              >
                Vegan <br /> BÁNH MÌ / SANDWICHES
              </Typography>
            </Grid>
            <Grid item lg={9} md={12} sm={12} xs={12}>
              <Grid container spacing={4}>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganSpecialCombo}
                    title="Vegan Special Combination"
                    subTitle="bánh mì ĐẶC BIỆT CHAY"
                    onAddToCart={() => navigate("/vegan-special-combo")}
                  />
                </Grid>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganLemongrassSteak}
                    title="Vegan Lemongrass Steak"
                    subTitle="bánh mì BÍT TẾT SẢ CHAY"
                    onAddToCart={() => navigate("/vegan-lemongrass-steak")}
                  />
                </Grid>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganFriedChicken}
                    title="VEGAN FRIED CHICKEN"
                    subTitle="bánh mì GÀ CHIÊN CHAY"
                    onAddToCart={() => navigate("/vegan-fried-chicken")}
                  />
                </Grid>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganGrilledPork}
                    title="Vegan Grilled Pork"
                    subTitle="bánh mì THỊT NƯỚNG CHAY"
                    onAddToCart={() => navigate("/vegan-grilled-pork")}
                  />
                </Grid>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganGrilledChicken}
                    title="Vegan Grilled Chicken"
                    subTitle="bánh mì GÀ NƯỚNG CHAY"
                    onAddToCart={() => navigate("/vegan-grilled-chicken")}
                  />
                </Grid>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganPorkMeatballs}
                    title="Vegan PORK MEATBALLS"
                    subTitle="bánh mì XÍU MẠI CHAY"
                    onAddToCart={() => navigate("/vegan-pork-meatballs")}
                  />
                </Grid>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganPateeMeatloaf}
                    title="Vegan Pâté Meat Loaf"
                    subTitle="bánh mì PÂTÉ CHẢ LỤA CHAY"
                    onAddToCart={() => navigate("/vegan-pate-meatloaf")}
                  />
                </Grid>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganShreddedPorkSkin}
                    title="Vegan Shredded Pork Skin"
                    subTitle="bánh mì BÌ CHAY"
                    onAddToCart={() => navigate("/vegan-shredded-pork-skin")}
                  />
                </Grid>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VietnameseBaguette}
                    title="Vietnamese Baguette"
                    subTitle="Bánh Mì Baguette"
                    onAddToCart={() => navigate("/vietnamese-baguette")}
                  />
                </Grid>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganHamCheeseCroissantSandwich}
                    title="Vegan Ham & Cheese Croissant Sandwich"
                    subTitle="Jambon & Phô Mai Chay"
                    onAddToCart={() => navigate("/vegan-ham-and-cheese-croissant-sandwich")}
                  />
                </Grid>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganChickenCroissantSandwich}
                    title="Vegan Chicken Croissant Sandwich"
                    subTitle="Gà Nướng Chay"
                    onAddToCart={() => navigate("/vegan-chicken-croissant-sandwich")}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Divider */}
      <Box sx={{ display: "flex", justifyContent: "center", background: "#000", pt: 10 }}>
        <Box sx={{ maxWidth: "1080px", width: "90%" }}>
          <Box sx={{ borderTop: "1px solid #fff" }} />
        </Box>
      </Box>

      {/* VEGAN PASTRIES */}
      <Box id="vegan-pastries" sx={{ display: "flex", justifyContent: "center", background: "#000", pt: 15 }}>
        <Box sx={{ maxWidth: "1080px", width: "90%" }}>
          <Grid container spacing={0}>
            <Grid item lg={3} md={12} sm={12} xs={12}>
              <Typography
                variant="h1"
                sx={{
                  mb: 3,
                  lineHeight: "1.2em",
                  px: 2,
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "30px",
                  textTransform: "uppercase",
                  textAlign: { xs: "center", lg: "left" },
                }}
              >
                VEGAN PASTRIES
              </Typography>
            </Grid>
            <Grid item lg={9} md={12} sm={12} xs={12}>
              <Grid container spacing={4}>
                {/* Vegan Croissant - FIRST CARD */}
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganCroissant}
                    title="Vegan Croissant"
                    onAddToCart={() => navigate("/vegan-croissant")}
                  />
                </Grid>

                {/* Vegan Pistachio Croissant - SECOND CARD */}
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganPistachioCroissant}
                    title="Vegan Pistachio Croissant"
                    onAddToCart={() => navigate("/vegan-pistachio-croissant")}
                  />
                </Grid>

                {/* Vegan Almond Croissant - THIRD CARD */}
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganAlmondCroissant}
                    title="Vegan Almond Croissant"
                    onAddToCart={() => navigate("/vegan-almond-croissant")}
                  />
                </Grid>

                {/* Vegan Chicken Croissant - THIRD CARD */}
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganChickenCroissant}
                    title="Vegan Chicken Croissant"
                    onAddToCart={() => navigate("/vegan-chicken-croissant")}
                  />
                </Grid>

                {/* Vegan Coconut Croissant */}
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganCoconutCroissant}
                    title="Vegan Coconut Croissant"
                    onAddToCart={() => navigate("/vegan-coconut-croissant")}
                  />
                </Grid>

                {/* Vegan Ham & Cheese Croissant */}
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganHamAndCheeseCroissant}
                    title="Vegan Ham & Cheese Croissant"
                    onAddToCart={() => navigate("/vegan-ham-and-cheese-croissant")}
                  />
                </Grid>

                {/* Vegan Ham & Cheese Jalapeño Croissant */}
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganHamAndCheeseCroissantJalapeno}
                    title="Vegan Ham & Cheese Jalapeño Croissant"
                    onAddToCart={() => navigate("/vegan-ham-and-cheese-jalapeno-croissant")}
                  />
                </Grid>

                {/* Vegan Guava Turnover */}
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganGuavaTurnover}
                    title="Vegan Guava Turnover"
                    onAddToCart={() => navigate("/vegan-guava-turnover")}
                  />
                </Grid>

                {/* Vegan Apple Turnover */}
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganAppleTurnover}
                    title="Vegan Apple Turnover"
                    onAddToCart={() => navigate("/vegan-apple-turnover")}
                  />
                </Grid>

                {/* Vegan Pineapple Turnover */}
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganPineappleTurnover}
                    title="Vegan Pineapple Turnover"
                    onAddToCart={() => navigate("/vegan-pineapple-turnover")}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Divider */}
      <Box sx={{ display: "flex", justifyContent: "center", background: "#000", pt: 10 }}>
        <Box sx={{ maxWidth: "1080px", width: "90%" }}>
          <Box sx={{ borderTop: "1px solid #fff" }} />
        </Box>
      </Box>

      {/* VEGAN VIETNAMESE SNACKS */}
      <Box
        id="vegan-vietnamese-snacks"
        sx={{ display: "flex", justifyContent: "center", background: "#000", pt: 15 }}
      >
        <Box sx={{ maxWidth: "1080px", width: "90%" }}>
          <Grid container spacing={0}>
            <Grid item lg={3} md={12} sm={12} xs={12}>
              <Typography
                variant="h1"
                sx={{
                  mb: 3,
                  lineHeight: "1.2em",
                  px: 2,
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "30px",
                  textTransform: "uppercase",
                  textAlign: { xs: "center", lg: "left" },
                }}
              >
                VEGAN Vietnamese <br /> SNACKS
              </Typography>
            </Grid>
            <Grid item lg={9} md={12} sm={12} xs={12}>
              <Grid container spacing={4}>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganVietnameseDonut}
                    title="Vegan Vietnamese Donut"
                    subTitle="Bánh Tiêu"
                    onAddToCart={() => navigate("/vegan-vietnamese-donut")}
                  />
                </Grid>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganSteamedBun}
                    title="Vegan Steamed Bun"
                    subTitle="Bánh Bao Chay"
                    onAddToCart={() => navigate("/vegan-steamed-bun")}
                  />
                </Grid>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganPatechaud}
                    title="Vegan Vietnamese Puff Pastry"
                    subTitle="Bánh Pâté Chaud Chay"
                    onAddToCart={() => navigate("/vegan-pate-chaud")}
                  />
                </Grid>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganBeefPuffPastry}
                    title="Vegan Beef Puff Pastry"
                    subTitle="Bánh Pâté Chaud Bò Chay"
                    onAddToCart={() => navigate("/vegan-beef-puff-pastry")}
                  />
                </Grid>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganSavoryEmpanada}
                    title="Vegan Savory Empanada"
                    subTitle="Bánh Quai Vạc Chay"
                    onAddToCart={() => navigate("/vegan-savory-empanada")}
                  />
                </Grid>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganSweetEmpanada}
                    title="Vegan Sweet Empanada"
                    subTitle="Durian, Taro, Mung Bean, Coconut"
                    onAddToCart={() => navigate("/vegan-sweet-empanada")}
                  />
                </Grid>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganSoftMiniBuns}
                    title="Vegan Soft Mini Buns"
                    subTitle="Coconut, Taro, Mung Bean, Durian"
                    onAddToCart={() => navigate("/vegan-soft-mini-buns")}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Divider */}
      <Box sx={{ display: "flex", justifyContent: "center", background: "#000", pt: 10 }}>
        <Box sx={{ maxWidth: "1080px", width: "90%" }}>
          <Box sx={{ borderTop: "1px solid #fff" }} />
        </Box>
      </Box>

      {/* VEGAN XÔI */}
      <Box id="vegan-xoi" sx={{ display: "flex", justifyContent: "center", background: "#000", pt: 15 }}>
        <Box sx={{ maxWidth: "1080px", width: "90%" }}>
          <Grid container spacing={0}>
            <Grid item lg={3} md={12} sm={12} xs={12}>
              <Typography
                variant="h1"
                sx={{
                  mb: 3,
                  lineHeight: "1.2em",
                  px: 2,
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "30px",
                  textTransform: "uppercase",
                  textAlign: { xs: "center", lg: "left" },
                }}
              >
                VEGAN XÔI
              </Typography>
            </Grid>
            <Grid item lg={9} md={12} sm={12} xs={12}>
              <Grid container spacing={4}>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={XoiVeganChicken}
                    title="Vegan Chicken Xôi"
                    subTitle="Xôi Gà Chay"
                    onAddToCart={() => navigate("/xoi-vegan-chicken")}
                  />
                </Grid>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={XoiPandan}
                    title="Pandan Sticky Rice"
                    subTitle="Xôi Lá Dứa"
                    onAddToCart={() => navigate("/xoi-pandan")}
                  />
                </Grid>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={XoiMagenta}
                    title="Magenta Leaves Sticky Rice"
                    subTitle="Xôi Lá Cẩm"
                    onAddToCart={() => navigate("/xoi-magenta-leaves")}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Divider */}
      <Box sx={{ display: "flex", justifyContent: "center", background: "#000", pt: 10 }}>
        <Box sx={{ maxWidth: "1080px", width: "90%" }}>
          <Box sx={{ borderTop: "1px solid #fff" }} />
        </Box>
      </Box>

      {/* VEGAN DRINKS */}
      <Box id="vegan-drinks" sx={{ display: "flex", justifyContent: "center", background: "#000", pt: 15, pb: 10 }}>
        <Box sx={{ maxWidth: "1080px", width: "90%" }}>
          <Grid container spacing={0}>
            <Grid item lg={3} md={12} sm={12} xs={12}>
              <Typography
                variant="h1"
                sx={{
                  mb: 3,
                  lineHeight: "1.2em",
                  px: 2,
                  pt: 1,
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "30px",
                  textTransform: "uppercase",
                  textAlign: { xs: "center", lg: "left" },
                }}
              >
                VEGAN Drinks
              </Typography>
            </Grid>
            <Grid item lg={9} md={12} sm={12} xs={12}>
              <Grid container spacing={4}>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VeganVietnameseCoffee}
                    title="Vegan Vietnamese COFFEE"
                    subTitle="CÀ PHÊ SỮA 'OAT' ĐÁ"
                    onAddToCart={() => navigate("/vegan-vietnamese-coffee")}
                  />
                </Grid>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={PennywortJuice}
                    title="Pennywort Juice"
                    subTitle="Rau Má"
                    onAddToCart={() => navigate("/rau-ma")}
                  />
                </Grid>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={VietnameseBeautyElixir}
                    title="Vietnamese Beauty Elixir"
                    subTitle="Chè Dưỡng Nhan"
                    onAddToCart={() => navigate("/vietnamese-beauty-elixir")}
                  />
                </Grid>
                <Grid item lg={4} md={6} sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MenuCard
                    img={SamBoLuong}
                    title="Vietnamese Herbal Dessert"
                    subTitle="Sâm Bổ Lượng"
                    onAddToCart={() => navigate("/sam-bo-luong")}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Footer />
    </>
  );
}

export default VeganMenu;